<template>
  <footer id="base-footer" class="base-footer">
    <div class="base-footer__content">
      <div class="base-container">
        <div class="footer-links footer-columns--desktop">
          <section v-for="section in footerLinkColumns" :key="section.key" class="footer-section">
            <h4 class="footer-section__title">{{ section.title }}</h4>
            <ul class="footer-section__list">
              <li v-for="item in section.items" :key="item.key" class="footer-section__item">
                <ContactUsPopup
                  v-if="item.action === 'contact-popup'"
                  class="footer-section__popup-trigger"
                >
                  <template #trigger>
                    <span
                      class="footer-section__link footer-section__link--interactive"
                      @click="handleFooterLinkClick(item)"
                    >
                      {{ item.label }}
                    </span>
                  </template>
                </ContactUsPopup>
                <ConsultantPopup
                  v-else-if="item.action === 'consultant-popup'"
                  class="footer-section__popup-trigger"
                  position="top"
                >
                  <template #trigger>
                    <span
                      class="footer-section__link footer-section__link--interactive"
                      @click="handleFooterLinkClick(item)"
                    >
                      {{ item.label }}
                    </span>
                  </template>
                </ConsultantPopup>
                <button
                  v-else-if="item.action === 'qiyu-chat'"
                  type="button"
                  class="footer-section__link footer-section__link--interactive"
                  @click="handleFooterQiyuChat(item.qiyuType)"
                >
                  {{ item.label }}
                </button>
                <a
                  v-else-if="item.href"
                  :href="item.href"
                  class="footer-section__link footer-section__link--interactive"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {{ item.label }}
                </a>
                <NuxtLink
                  v-else-if="item.to"
                  :to="item.to"
                  class="footer-section__link footer-section__link--interactive"
                  @click="handleFooterLinkClick(item)"
                >
                  {{ item.label }}
                </NuxtLink>
                <span v-else class="footer-section__link">{{ item.label }}</span>
              </li>
            </ul>
          </section>

          <section class="footer-app">
            <div class="footer-app__codes">
              <div v-for="item in footerQrCodes" :key="item.key" class="footer-app__code-item">
                <div class="footer-app__qr-wrap">
                  <img :src="withBase(item.src)" :alt="item.alt" class="footer-app__qr" />
                </div>
                <span class="footer-app__label">{{ item.label }}</span>
              </div>
            </div>
          </section>
        </div>

        <div class="footer-columns footer-columns--mobile">
          <section
            v-for="section in mobileFooterSections"
            :key="section.title"
            class="footer-section"
          >
            <button
              :aria-expanded="expandedGroups[section.title] ? 'true' : 'false'"
              type="button"
              class="footer-section__trigger"
              @click="toggleGroup(section.title)"
            >
              <span class="footer-section__title">{{ section.title }}</span>
              <img
                :src="
                  withBase(
                    expandedGroups[section.title]
                      ? '/images/footer/del.svg'
                      : '/images/footer/plus.svg'
                  )
                "
                alt=""
                class="footer-section__toggle-icon"
                width="16"
                height="16"
              />
            </button>
            <ul v-show="expandedGroups[section.title]" class="footer-section__list">
              <li v-for="item in section.items" :key="item.key" class="footer-section__item">
                <ContactUsPopup
                  v-if="item.action === 'contact-popup'"
                  class="footer-section__popup-trigger"
                >
                  <template #trigger>
                    <span
                      class="footer-section__link footer-section__link--interactive"
                      @click="handleFooterLinkClick(item)"
                    >
                      {{ item.label }}
                    </span>
                  </template>
                </ContactUsPopup>
                <ConsultantPopup
                  v-else-if="item.action === 'consultant-popup'"
                  class="footer-section__popup-trigger"
                  position="top"
                >
                  <template #trigger>
                    <span
                      class="footer-section__link footer-section__link--interactive"
                      @click="handleFooterLinkClick(item)"
                    >
                      {{ item.label }}
                    </span>
                  </template>
                </ConsultantPopup>
                <button
                  v-else-if="item.action === 'qiyu-chat'"
                  type="button"
                  class="footer-section__link footer-section__link--interactive"
                  @click="handleFooterQiyuChat(item.qiyuType)"
                >
                  {{ item.label }}
                </button>
                <a
                  v-else-if="item.href"
                  :href="item.href"
                  class="footer-section__link footer-section__link--interactive"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {{ item.label }}
                </a>
                <NuxtLink
                  v-else-if="item.to"
                  :to="item.to"
                  class="footer-section__link footer-section__link--interactive"
                  @click="handleFooterLinkClick(item)"
                >
                  {{ item.label }}
                </NuxtLink>
                <span v-else class="footer-section__link">{{ item.label }}</span>
              </li>
            </ul>
          </section>
        </div>
      </div>
    </div>

    <div class="base-footer__meta">
      <div class="base-container footer-mobile-meta">
        <FooterSocials class="footer-mobile-meta__socials" variant="mobile" />

        <div class="footer-mobile-meta__brand">
          <NuxtLink to="/" class="footer-mobile-meta__logo" aria-label="返回首页">
            <img
              :src="withBase('/images/brand/logo-primary.svg')"
              alt="品牌标识"
              class="footer-mobile-meta__logo-image"
            />
          </NuxtLink>
          <div class="footer-mobile-meta__links">
            <span
              v-for="item in FOOTER_META_LINKS"
              :key="item.key"
              class="footer-mobile-meta__links-item"
            >
              <a
                v-if="item.href"
                :href="item.href"
                class="footer-mobile-meta__link"
                rel="noopener noreferrer"
                target="_blank"
              >
                {{ item.label }}
              </a>
              <NuxtLink v-else-if="item.to" :to="item.to" class="footer-mobile-meta__link">
                {{ item.label }}
              </NuxtLink>
              <span v-else class="footer-mobile-meta__link">{{ item.label }}</span>
            </span>
          </div>
        </div>

        <p class="footer-mobile-meta__legal">
          <a
            href="https://beian.miit.gov.cn/"
            class="footer-legal__link"
            rel="noopener noreferrer"
            target="_blank"
          >
            粤ICP备05011718号
          </a>
          版权所有 © 1999-{{ currentYear }} 深圳市吉祥腾达科技有限公司
        </p>
      </div>

      <div class="base-container base-footer__meta-inner">
        <div class="footer-meta-top">
          <div class="footer-brand">
            <NuxtLink to="/" class="footer-brand__logo" aria-label="返回首页">
              <img
                :src="withBase('/images/brand/logo-primary.svg')"
                alt="品牌标识"
                class="footer-brand__logo-image"
              />
            </NuxtLink>
            <div class="footer-meta-links">
              <span
                v-for="item in FOOTER_META_LINKS"
                :key="item.key"
                class="footer-meta-links__item"
              >
                <a
                  v-if="item.href"
                  :href="item.href"
                  class="footer-meta-links__link"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {{ item.label }}
                </a>
                <NuxtLink v-else-if="item.to" :to="item.to" class="footer-meta-links__link">
                  {{ item.label }}
                </NuxtLink>
                <template v-else>{{ item.label }}</template>
              </span>
            </div>
          </div>

          <FooterSocials variant="desktop" />
        </div>

        <div class="footer-meta-bottom">
          <div class="footer-legal">
            <a
              href="https://beian.miit.gov.cn/"
              class="footer-legal__link"
              rel="noopener noreferrer"
              target="_blank"
            >
              粤ICP备05011718号
            </a>
            <span>版权所有 © 1999-{{ currentYear }} 深圳市吉祥腾达科技有限公司</span>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { BaseMessage } from '~/utils/baseMessage'
import { computed, onMounted, ref, watch } from 'vue'
import ConsultantPopup from '~/components/base/consultant/ConsultantWrapper.vue'
import ContactUsPopup from '~/components/base/contact/ContactUsPopup.vue'
import FooterSocials from '~/components/base/footer/FooterSocials.vue'
import { useQiyuChat } from '~/composables/useQiyuChat'
import { WECHAT_OFFICIAL_ACCOUNT_QR_CODE, WECHAT_CHANNELS_QR_CODE } from '~/utils/imsRuntime'
import { trackClickEvent } from '~/composables/usePageTracking'
import { useCategoryStore } from '~/store/common/category'
import { useUserStore } from '~/store/common/user'
import { baseApi } from '~/api/base'
import { websiteApi } from '~/api/website'
import { buildCategoryTreeFromApi } from '~/utils/store/categoryTree'
import { mapFooterCategoryNavSections } from '~/utils/store/topCategoryNav'
import { createPublicContentSsrRequestConfig } from '~/utils/website/ssrRequestPolicy'

/**
 * @typedef {Object} FooterSectionItem
 * @property {string} key
 * @property {string} label
 * @property {string|Object|null} [to]
 * @property {string} [href]
 * @property {'contact-popup'|'consultant-popup'|'qiyu-chat'} [action]
 * @property {'sales'|'afterSales'|'scheme'} [qiyuType]
 */

const SUPPLIER_PORTAL_HREF = 'https://d2jr7195bqwu.cp.xiekeyun.com/home'

const { withBase } = useBasePath()
const { openQiyuChat } = useQiyuChat()
const categoryStore = useCategoryStore()
const userStore = useUserStore()
const isFooterMounted = ref(false)
const footerCategorySsrSnapshotActive = ref(import.meta.server || useNuxtApp().isHydrating)
const footerSimulatorSsrSnapshotActive = ref(import.meta.server || useNuxtApp().isHydrating)
const productSimulatorVisibleOnClient = ref(false)
// TEMP: 底部栏产品模拟器中心入口暂时隐藏，恢复时改为 true。
const SHOW_PRODUCT_SIMULATOR_ENTRY = true

/** @type {import('vue').ComputedRef<Array<{ key: string, src: string, label: string, alt: string }>>} */
const footerQrCodes = computed(() => [
  {
    key: 'wechat-channels',
    src: WECHAT_CHANNELS_QR_CODE,
    label: '腾达视频号',
    alt: '腾达视频号二维码',
  },
  {
    key: 'wechat-official',
    src: WECHAT_OFFICIAL_ACCOUNT_QR_CODE,
    label: '腾达公众号',
    alt: '腾达公众号二维码',
  },
])

const { data: footerNavigation } = await useAsyncData(
  'footer-public-navigation',
  async () => {
    if (import.meta.client) {
      return { categoryTree: [], productSimulatorVisible: false }
    }
    if (userStore.isLoggedIn) {
      return { categoryTree: [], productSimulatorVisible: false }
    }

    const requestConfig = createPublicContentSsrRequestConfig({ timeout: 3_000 })
    const [categoryResult, simulatorResult] = await Promise.allSettled([
      baseApi.getCategoryProdTree(requestConfig),
      SHOW_PRODUCT_SIMULATOR_ENTRY
        ? websiteApi.getProductSimulatorList({ page: 1, pageSize: 1 }, requestConfig)
        : Promise.resolve(null),
    ])

    return {
      categoryTree:
        categoryResult.status === 'fulfilled' ? buildCategoryTreeFromApi(categoryResult.value) : [],
      productSimulatorVisible:
        simulatorResult.status === 'fulfilled' && Number(simulatorResult.value?.total) > 0,
    }
  },
  usePublicContentAsyncDataOptions({
    default: () => ({ categoryTree: [], productSimulatorVisible: false }),
  })
)

const footerCategorySections = computed(() => {
  if (footerCategorySsrSnapshotActive.value) {
    return mapFooterCategoryNavSections(footerNavigation.value?.categoryTree || [])
  }
  return isFooterMounted.value ? mapFooterCategoryNavSections(categoryStore.categoryList) : []
})
const productSimulatorVisible = computed(() =>
  footerSimulatorSsrSnapshotActive.value
    ? footerNavigation.value?.productSimulatorVisible === true
    : productSimulatorVisibleOnClient.value
)

onMounted(() => {
  isFooterMounted.value = true
  categoryStore
    .fetchCategoryInfo()
    .then(() => {
      footerCategorySsrSnapshotActive.value = false
    })
    .catch((error) => {
      console.error('Failed to fetch footer category info:', error)
      BaseMessage.error('底部分类加载失败')
    })
  if (SHOW_PRODUCT_SIMULATOR_ENTRY) {
    websiteApi
      .getProductSimulatorList({ page: 1, pageSize: 1 })
      .then((result) => {
        productSimulatorVisibleOnClient.value = Number(result?.total) > 0
        footerSimulatorSsrSnapshotActive.value = false
      })
      .catch(() => {})
  }
})

const DELISTED_ROUTE = { path: '/website/service/delisted' }
// TEMP: 底部栏代理商证书查询入口暂时隐藏，恢复时改为 true。
const SHOW_AGENCY_CERTIFICATE_QUERY_ENTRY = true

const helpSupportItems = computed(() => [
  { key: 'help-download', label: '下载中心', to: { path: '/website/service/download' } },
  { key: 'help-center', label: '帮助中心', to: { path: '/website/service/help' } },
  { key: 'help-delisted', label: '退市专区', to: DELISTED_ROUTE },
  { key: 'help-notices', label: '公告中心', to: { path: '/website/service/announcement' } },
  ...(SHOW_PRODUCT_SIMULATOR_ENTRY && productSimulatorVisible.value
    ? [
        {
          key: 'help-product-simulator',
          label: '产品模拟器中心',
          to: { path: '/website/service/help', query: { contentType: 'product-simulator' } },
        },
      ]
    : []),
])

const SERVICE_CONSULT_ITEMS = [
  { key: 'service-contact', label: '联系我们', action: 'contact-popup' },
  { key: 'service-advisor', label: '专属顾问', action: 'consultant-popup' },
  { key: 'service-solution', label: '解决方案咨询', action: 'qiyu-chat', qiyuType: 'scheme' },
  { key: 'service-product', label: '产品咨询', action: 'qiyu-chat', qiyuType: 'sales' },
  { key: 'service-after-sales', label: '售后咨询', action: 'qiyu-chat', qiyuType: 'afterSales' },
]

const FOOTER_META_LINKS = [
  { key: 'about', label: '关于我们', to: '/website/profile' },
  ...(SHOW_AGENCY_CERTIFICATE_QUERY_ENTRY
    ? [{ key: 'agency-query', label: '代理商查询', to: '/store/agency-query' }]
    : []),
  { key: 'supplier', label: '供应商门户', href: SUPPLIER_PORTAL_HREF },
  {
    key: 'employee-verification',
    label: '员工信息核验平台',
    href: 'https://daka.tenda.cn:80/verification/',
  },
]

const currentYear = new Date().getFullYear()

/**
 * @description Build a store category route for footer quick links.
 * @param {string|number} primaryCategoryId - Root category id.
 * @param {string|number} secondaryCategoryId - Child category id.
 * @returns {{ path: string, query: { categoryId: string, secondaryCategoryId: string } }|null}
 */
const buildCategoryRoute = (primaryCategoryId, secondaryCategoryId) => {
  if (!primaryCategoryId || !secondaryCategoryId) return null

  return {
    path: '/store/products',
    query: {
      categoryId: String(primaryCategoryId),
      secondaryCategoryId: String(secondaryCategoryId),
    },
  }
}

const footerLinkColumns = computed(() => {
  const dynamicSections = footerCategorySections.value.map((section) => ({
    key: `category-${section.key}`,
    title: section.label,
    items: section.categories.map((item) => ({
      key: `dynamic-${section.key}-${item.categoryId}`,
      label: item.categoryName,
      categoryId: item.categoryId,
      to: buildCategoryRoute(section.categoryId, item.categoryId),
    })),
  }))

  return [
    ...dynamicSections,
    {
      key: 'help-support',
      title: '帮助与支持',
      items: helpSupportItems.value,
    },
    {
      key: 'service-consult',
      title: '服务咨询',
      items: SERVICE_CONSULT_ITEMS,
    },
  ]
})

const mobileFooterSections = computed(() => {
  const mobileDynamicSections = footerCategorySections.value.map((section) => ({
    title: section.mobileLabel,
    items: section.categories.map((item) => ({
      key: `mobile-dynamic-${section.key}-${item.categoryId}`,
      label: item.categoryName,
      categoryId: item.categoryId,
      to: buildCategoryRoute(section.categoryId, item.categoryId),
    })),
  }))

  return [
    ...mobileDynamicSections,
    {
      title: '服务咨询',
      items: SERVICE_CONSULT_ITEMS.map((item) => ({
        ...item,
        key: `mobile-${item.key}`,
      })),
    },
    {
      title: '帮助与支持',
      items: helpSupportItems.value.map((item) => ({
        ...item,
        key: `mobile-${item.key}`,
      })),
    },
  ]
})

const expandedGroups = ref({})

watch(
  mobileFooterSections,
  (sections) => {
    expandedGroups.value = sections.reduce((acc, section) => {
      acc[section.title] = expandedGroups.value[section.title] ?? false
      return acc
    }, {})
  },
  { immediate: true }
)

/**
 * @description Toggle mobile footer section visibility.
 * @param {string} title - Current section title.
 * @returns {void}
 */
const toggleGroup = (title) => {
  expandedGroups.value[title] = !expandedGroups.value[title]
}

/**
 * @description 页脚服务咨询区打开七鱼客服
 * @param {'sales'|'afterSales'|'scheme'} qiyuType - 客服类型
 * @returns {void}
 */
const handleFooterQiyuChat = (qiyuType) => {
  if (!qiyuType) return
  const eventMap = {
    scheme: 'pv_BottomNavigation_SchemeConsultation_click',
    sales: 'pv_BottomNavigation_ProductConsultation_click',
    afterSales: 'pv_BottomNavigation_After-salesConsultation_click',
  }
  if (eventMap[qiyuType]) {
    trackClickEvent({
      pvEvent: eventMap[qiyuType],
      targetType: 'bottom_navigation',
      targetId: qiyuType,
      targetName:
        qiyuType === 'scheme'
          ? '解决方案咨询'
          : qiyuType === 'afterSales'
            ? '售后咨询'
            : '产品咨询',
      pageName: '底部导航',
      properties: {
        link_key: qiyuType,
        link_name:
          qiyuType === 'scheme'
            ? '解决方案咨询'
            : qiyuType === 'afterSales'
              ? '售后咨询'
              : '产品咨询',
      },
    })
  }
  openQiyuChat({ type: qiyuType, from: 'footer-service-consult' })
}

/**
 * @description 底部导航分类/帮助链接点击埋点
 * @param {Object} item
 * @returns {void}
 */
const handleFooterLinkClick = (item) => {
  const rawKey = String(item?.key || '')
  const normalizedKey = rawKey.replace(/^mobile-/, '')
  const eventMap = {
    'help-download': 'pv_BottomNavigation_DownloadCenter_click',
    'help-center': 'pv_BottomNavigation_HelpCenter_click',
    'help-delisted': 'pv_BottomNavigation_DelistingCenter_click',
    'help-notices': 'pv_BottomNavigation_NoticeCenter_click',
    'service-contact': 'pv_BottomNavigation_ContactUs_click',
    'service-advisor': 'pv_BottomNavigation_ExclusiveConsultant_click',
    'service-solution': 'pv_BottomNavigation_SchemeConsultation_click',
    'service-product': 'pv_BottomNavigation_ProductConsultation_click',
    'service-after-sales': 'pv_BottomNavigation_After-salesConsultation_click',
  }

  if (eventMap[normalizedKey]) {
    trackClickEvent({
      pvEvent: eventMap[normalizedKey],
      targetType: 'bottom_navigation',
      targetId: normalizedKey,
      targetName: item.label || '',
      pageName: '底部导航',
      properties: {
        link_key: normalizedKey,
        link_name: item.label || '',
      },
    })
    return
  }

  if (rawKey.startsWith('dynamic-') || rawKey.startsWith('mobile-dynamic-')) {
    const categoryIdMatch = rawKey.match(/(?:dynamic|mobile-dynamic)-[^-]+-(.+)$/)
    const categoryId = item.categoryId || categoryIdMatch?.[1] || ''
    trackClickEvent({
      pvEvent: 'pv_BottomNavigation_Category_click',
      targetType: 'bottom_navigation_category',
      targetId: String(categoryId || item.label || normalizedKey),
      targetName: item.label || '',
      pageName: '底部导航',
      properties: {
        category_id: String(categoryId || ''),
        category_name: item.label || '',
      },
    })
  }
}
</script>

<style lang="scss" scoped>
.base-footer {
  background-color: $bg-white;
  color: $text-title-color;

  &__content {
    padding: 0;
  }

  &__meta {
    padding-top: $spacing-xl;

    &-inner {
      display: flex;
      flex-direction: column;
      gap: $spacing-xl;
      padding-bottom: $spacing-xl;
    }
  }
}

.footer-links {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr)) 280px;
  gap: 12px;
  padding: $spacing-xl 0;
  border-bottom: 1px solid $border-light-color;
  align-items: start;
}

.footer-columns {
  &--desktop {
    display: none;
  }

  &--mobile {
    display: none;
  }
}

.footer-links.footer-columns--desktop {
  display: grid;
}

.footer-mobile-meta {
  display: none;
}

.footer-section {
  display: flex;
  flex-direction: column;
  gap: $spacing-md;
  min-width: 0;

  &__popup-trigger {
    width: 100%;
  }

  &__title {
    margin: 0;
    color: $text-title-color;
    font-size: $font-size-title-3;
    font-weight: $font-weight-medium;
    line-height: $line-height-title-3;
  }

  &__trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $spacing-sm;
    width: 100%;
    padding: 0;
    border: none;
    background: transparent;
    text-align: left;
  }

  &__toggle-icon {
    flex-shrink: 0;
    display: block;
    width: 16px;
    height: 16px;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: $spacing-xs;
    padding: 0;
    margin: 0;
    list-style: none;
  }

  &__item {
    margin: 0;
  }

  &__link {
    display: inline-flex;
    color: $text-content-color;
    font-size: $font-size-body-1;
    line-height: $line-height-md;
    text-decoration: none;
    cursor: default;

    &--interactive {
      cursor: pointer;
      transition: color 0.2s ease;

      &:is(button) {
        padding: 0;
        border: none;
        background: transparent;
        text-align: left;
      }

      &:hover {
        color: $primary-color;
      }
    }
  }
}

.footer-app {
  display: flex;
  flex-direction: column;
  gap: $spacing-md;
  padding-top: $line-height-title-3 + $spacing-md;
  width: 280px;
  flex-shrink: 0;

  &__codes {
    display: flex;
    align-items: flex-end;
    gap: $spacing-xl;
  }

  &__code-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    width: 110px;
  }

  &__qr-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 110px;
    height: 110px;
    border-radius: $radius-6;
    background-color: $bg-white;
    overflow: hidden;
    flex-shrink: 0;
  }

  &__qr {
    display: block;
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  &__label {
    color: $text-content-color;
    font-size: $font-size-body-1;
    line-height: $line-height-md;
    text-align: center;
  }
}

.footer-meta-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $spacing-xl;
  padding-bottom: $spacing-xl;
  border-bottom: 1px solid $border-light-color;
}

.footer-brand {
  display: flex;
  align-items: center;
  gap: $spacing-xl;

  &__logo {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    text-decoration: none;
  }

  &__logo-image {
    width: 84px;
    height: 24px;
    object-fit: contain;
  }
}

.footer-meta-links {
  display: flex;
  align-items: center;
  gap: $spacing-md;
  flex-wrap: wrap;

  &__item {
    color: $text-title-color;
    font-size: $font-size-body-1;
    line-height: $line-height-md;
  }

  &__link {
    color: inherit;
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
      color: $primary-color;
    }
  }
}

.footer-meta-bottom {
  display: flex;
  align-items: center;
}

.footer-legal {
  display: flex;
  align-items: center;
  gap: $spacing-xs;
  flex-wrap: wrap;
  color: $text-subtitle-color;
  font-family: $font-family-pingfang-sc;
  font-size: $font-size-body-3;
  font-weight: $font-weight-regular;
  line-height: 1.4;

  &__link {
    color: inherit;
    text-decoration: none;

    &:hover {
      color: $primary-color;
    }
  }
}

@include respond-to-max('xl') {
  .footer-links.footer-columns--desktop {
    grid-template-columns: repeat(5, minmax(0, 1fr)) calc(110px + $spacing-lg);
  }

  .footer-app {
    width: 110px;

    &__codes {
      flex-direction: column;
      align-items: center;
      gap: $spacing-lg;
    }
  }
}

@include respond-to-max('lg') {
  .base-footer {
    .base-container {
      padding-left: $spacing-xl;
      padding-right: $spacing-xl;
    }
  }

  .base-footer {
    &__content {
      padding: 0;
    }

    &__meta {
      padding-top: 0;
      border-top: none;
    }
  }

  .footer-links.footer-columns--desktop {
    display: none;
  }

  .footer-columns--mobile {
    display: flex;
    flex-direction: column;
    gap: 0;
    padding: 0;
  }

  .footer-section {
    gap: 0;
    padding: 0;
    border-bottom: 1px solid $border-muted-color;

    &__trigger {
      height: 48px;
      padding: 0;
    }

    &__title {
      font-size: 14px;
      line-height: normal;
      font-weight: 600;
      color: $color-black;
    }

    &__toggle-icon {
      flex-shrink: 0;
    }

    &__list {
      gap: 12px;
      padding: 0 0 16px $spacing-sm;
    }

    &__link {
      color: $text-content-color;
      font-size: 13px;
      line-height: 20px;
    }
  }

  .base-footer__meta-inner {
    display: none;
  }

  .footer-mobile-meta {
    display: flex;
    flex-direction: column;
    padding-top: 0;
    padding-bottom: 24px;

    &__brand {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 13px;
      margin-top: $spacing-md;
      padding-top: $spacing-md;
      border-top: 1px solid $border-muted-color;
    }

    &__logo {
      display: inline-flex;
      align-items: center;
      flex-shrink: 0;
      text-decoration: none;
    }

    &__logo-image {
      width: 85px;
      height: 24px;
      object-fit: contain;
    }

    &__links {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: $spacing-sm;
    }

    :deep(.footer-mobile-meta__socials.footer-socials--mobile) {
      gap: $spacing-md;
      margin: $spacing-md 0 0;
      padding: 0;
    }

    &__links-item {
      color: $text-title-color;
      font-size: $font-size-body-1;
      line-height: $line-height-md;
    }

    &__link {
      color: inherit;
      font-size: inherit;
      line-height: inherit;
      text-decoration: none;

      &:active {
        opacity: 0.7;
      }
    }

    &__legal {
      margin: 12px 0 0;
      color: $text-subtitle-color;
      font-family: $font-family-pingfang-sc;
      font-size: $font-size-body-3;
      font-weight: $font-weight-regular;
      line-height: 1.4;
    }
  }
}
</style>
